import { useCallback, useMemo, useState, useEffect } from "react";
import { useSelector } from "react-redux";
import { FormikHelpers, FormikValues } from "formik";
import dropdownService from "../../../../services/api/service/dropdownService";
import documentsServices from "../../../../services/api/service/documentService";
import postsServices from "../../../../services/api/service/postsService";
import losServices from "../../../../services/api/service/losService";
import applicantDetailsServices from "../../../../services/api/service/applicantDetailsService";
import { fieldsList } from "../../../../utils/coApplicantsFields";
import type { RootState } from "../../../../services/redux/store";
import { setStatusPopup } from "../../../../services/redux/slices/statusPopupSlice";
import {
  setDocumentApplicantType,
  setEncryptionKey,
  removeFormDataKeys,
} from "../../../../services/redux/slices/stepperDynamicFormSlice";
import { encryptData } from "../../../../utils/crypto";
import { getSanitizedPublicKeyFromResponse } from "../../../../utils/piiKey";
import { buildCoApplicantValidationSchema } from "./coApplicantValidationSchema";
import { createCoApplicantFields } from "./coApplicantFieldsFactory";
import { submitCoApplicant } from "./coApplicantSubmit";
import { usePanOtp } from "./usePanOtp";
import type { CoApplicantField } from "../types";
import { useApplicantMasterData } from "../../../../hooks/useApplicantMasterData";

export function useCoApplicantFlow({
  dispatch,
  applicants,
  tabIndex,
  setTabIndex,
  applicationNumber,
  submissionTrackingId,
  userProfile,
  fetchUserApplicationData,
  applicationId,
  handleFetchApplicationDetail,
  refreshDocuments,
  setPartnerStartedJourney,
}: {
  dispatch: any;
  applicants: string[];
  tabIndex: number;
  setTabIndex: (n: number) => void;
  applicationNumber: any;
  submissionTrackingId: any;
  userProfile: any;
  fetchUserApplicationData: any;
  applicationId?: string;
  handleFetchApplicationDetail: () => void;
  refreshDocuments: () => void;
  setPartnerStartedJourney: any;
}) {
  const encryptionKey = useSelector(
    (state: RootState) => state.stepper.encryptionKey
  );
  const formData = useSelector((state: RootState) => state.stepper.formData);

  const fetchSanitizedPiiPublicKey = useCallback(async () => {
    if (encryptionKey) {
      return getSanitizedPublicKeyFromResponse(encryptionKey);
    }
    const piiKeyResponse: any = await losServices.getPiiKey();
    if (piiKeyResponse?.status !== 200) return "";

    const rawKey = piiKeyResponse?.data?.p_k;
    const rawKeyStr = typeof rawKey === "string" ? rawKey : "";
    dispatch(setEncryptionKey(rawKeyStr));

    return getSanitizedPublicKeyFromResponse(rawKey);
  }, [encryptionKey, dispatch]);

  const [coApplicantFields, setCoApplicantFields] = useState<any>([]);
  const [coApplicantNumbers, setCoApplicantNumbers] = useState<number>(0);
  const getCoApplicantNumbers = useMemo(() => coApplicantNumbers, [coApplicantNumbers]);

  const [coApplicantFieldsLoading, setCoApplicantFieldsLoading] = useState(false);
  const [isAddingCoApplicant, setIsAddingCoApplicant] = useState(false);
  const [isCoApplicantModalOpen, setIsCoApplicantModalOpen] = useState(false);

  const [coApplicantFieldsIds, setCoApplicantFieldsIds] = useState<any>([]);
  const [deleteFieldIds, setDeleteFieldIds] = useState<any>([]);

  const [isCoApplicantPanVerified, setIsCoApplicantPanVerified] = useState(false);
  const [coApplicantPanLoading, setCoApplicantPanLoading] = useState(false);
  const [aadhaarSeedingStatus, setAadhaarSeedingStatus] = useState<string>("");
  const [empTypeError, setEmpTypeError] = useState<string>("");

  // Co-Applicant Modal (legacy local mirror used by OTP flow + masked display)
  const [coApplicantFormData, setCoApplicantFormData] = useState<any>({
    relationship: "",
    firstName: "",
    lastName: "",
    dob: null as Date | null,
    panNumber: "",
    email: "",
    mobileNumber: "",
  });

  const [SalutationList, setSalutationList] = useState<{ value: string; label: string }[]>([]);
  const [journeyPennantRelationshipMaster, setJourneyPennantRelationshipMaster] = useState<
    { value: string; label: string }[]
  >([]);
  const {
    countryOptions,
    nationalityOptions,
    maritalStatusOptions,
    fetchCitiesByCountryState,
  } = useApplicantMasterData();

  const panOtp = usePanOtp({ dispatch, coApplicantFormData });

  // useEffect(() => {
  //   fetchSalutations();
  //   handleJourneyPennantRelationshipMaster();
  // }, []);

  const fetchSalutations = useCallback(async () => {
    try {
      const salutationResponse = await dropdownService.getSalutations();
      const salutationsOptions = salutationResponse.data.map((sal: any) => ({
        value: sal.salutationCode,
        label: sal.salutationDesc,
      }));
      setSalutationList(salutationsOptions);
      return salutationsOptions;
    } catch {
      dispatch(
        setStatusPopup({
          status: "error",
          message: "Failed to get salutations !",
        })
      );
      return [];
    }
  }, [dispatch]);

  const handleJourneyPennantRelationshipMaster = useCallback(async () => {
    try {
      const salutationList = await fetchSalutations();
      const response: any = await dropdownService.getJourneyPennantRelationshipMaster();
      if (response?.status === "success") {
        const relationshipOptions = (response?.data ?? []).map((item: any) => ({
          value: item.codeInPennant ?? "",
          label: item.descInPennant ?? "",
        }));
        setJourneyPennantRelationshipMaster(relationshipOptions);
        return { relationshipOptions, salutationList };
      }
      return { relationshipOptions: [], salutationList };
    } catch {
      dispatch(
        setStatusPopup({
          status: "error",
          message: "Failed to get relationship list !",
        })
      );
      return { relationshipOptions: [], salutationList: [] };
    }
  }, [dispatch, fetchSalutations]);

  const addCoApplicantFields = useCallback(
    async (
      coApplicantNumbersValue: number,
      relationshipList: { value: string; label: string }[] = [],
      salutationListOverride: { value: string; label: string }[] = [],
      nationalityOptions: { value: string; label: string }[] = [],
      maritalStatusOptions: { value: string; label: string }[] = [],
      countryOfBirthOptions: { value: string; label: string }[] = [],
      cityOfBirthOptions: { value: string; label: string }[] = []
    ) => {
      try {
        const apiFields = createCoApplicantFields({
          coApplicantNumbersValue,
          salutationList:
            (salutationListOverride?.length ?? 0) > 0
              ? salutationListOverride
              : SalutationList,
          relationshipList: (relationshipList?.length ?? 0) > 0 ? relationshipList : journeyPennantRelationshipMaster,
          nationalityOptions,
          maritalStatusOptions,
          countryOfBirthOptions,
          cityOfBirthOptions,
        });
        setCoApplicantFields(apiFields);
      } finally {
        setIsAddingCoApplicant(false);
      }
    },
    [
      SalutationList,
      journeyPennantRelationshipMaster,
      nationalityOptions,
      maritalStatusOptions,
      countryOptions,
    ]
  );

  const getInitialValues = useCallback(() => {
    const initialValues: any = {};
    coApplicantFields.forEach((field: any) => {
      switch (field.type) {
        case "date":
          initialValues[field.name] = null;
          break;
        case "select":
        case "dropdown":
          initialValues[field.name] = "";
          break;
        default:
          initialValues[field.name] = "";
      }
    });
    return initialValues;
  }, [coApplicantFields]);

  const coApplicantValidationSchema = useMemo(() => {
    return buildCoApplicantValidationSchema(coApplicantFields, (getCoApplicantNumbers + 1) > 1 ? true : false);
  }, [coApplicantFields]);

  const handleCloseCoApplicantModal = useCallback(() => {
    setIsCoApplicantModalOpen(false);
    setIsCoApplicantPanVerified(false);
    setCoApplicantFormData({
      relationship: "",
      firstName: "",
      lastName: "",
      dob: null,
      panNumber: "",
      email: "",
      mobileNumber: "",
    });
    panOtp.setIsPanVerified(false);
  }, [panOtp]);

  const handleCoApplicantFormChange = useCallback((field: string, value: any) => {
    setCoApplicantFormData((prev: any) => ({ ...prev, [field]: value }));
    if (field.includes("PanNumber") && isCoApplicantPanVerified) {
      setIsCoApplicantPanVerified(false);
    }
    const countryMatch = String(field).match(/^basicCoApplicant(\d+)CountryOfBirth$/);
    if (!countryMatch) return;

    const coApplicantNumber = countryMatch[1];
    const countryFieldName = `basicCoApplicant${coApplicantNumber}CountryOfBirth`;
    const countryFieldConfig = coApplicantFields.find(
      (f: any) => f?.name === countryFieldName
    );
    const countryOptions = countryFieldConfig?.options?.options ?? [];
    const matchedCountryOption = countryOptions.find(
      (opt: any) => String(opt?.value ?? opt) === String(value)
    );
    const selectedCountry = String(
      value?.label ??
        matchedCountryOption?.label ??
        matchedCountryOption?.value ??
        value ??
        ""
    ).trim();
    if (!selectedCountry) {
      setCoApplicantFields((prev: any[]) =>
        prev.map((f: any) =>
          f.name === `basicCoApplicant${coApplicantNumber}CityOfBirth`
            ? { ...f, options: { options: [] } }
            : f
        )
      );
      return;
    }

    fetchCitiesByCountryState(selectedCountry).then((cities) => {
      setCoApplicantFields((prev: any[]) =>
        prev.map((f: any) =>
          f.name === `basicCoApplicant${coApplicantNumber}CityOfBirth`
            ? { ...f, options: { options: cities } }
            : f
        )
      );
    });
  }, [fetchCitiesByCountryState, isCoApplicantPanVerified]);

  const handleGetApplicationStatus = useCallback(async () => {
    try {
      const response: any = await losServices.getApplicationStatus(submissionTrackingId);
      if (response?.status === 200) {
        if (response?.data?.data?.partnerStartedJourney) {
          dispatch(
            setStatusPopup({
              status: "info",
              message: "You cannot add new co applicant !",
            })
          );
          setPartnerStartedJourney(response?.data?.data?.partnerStartedJourney);
        } else {
          setIsCoApplicantModalOpen(true);
        }
        setIsAddingCoApplicant(false);
      }
    } catch (error: any) {
      setIsAddingCoApplicant(false);
      dispatch(
        setStatusPopup({
          status: "error",
          message: error?.response?.data?.message,
        })
      );
    }
  }, [dispatch, setPartnerStartedJourney, submissionTrackingId]);

  /** Called when user clicks "Add Co-Applicant": calls getCoApplicantCount API, then opens modal on success. */
  const handleGetCoApplicantCount = useCallback(async () => {
    if (!submissionTrackingId) {
      dispatch(
        setStatusPopup({
          status: "error",
          message: "Application tracking ID is missing. Cannot add co-applicant.",
        })
      );
      return;
    }
    setIsAddingCoApplicant(true);
    try {
      const { relationshipOptions, salutationList } =
        await handleJourneyPennantRelationshipMaster();
      const response: any = await losServices.getCoApplicantCount({
        trackingId: submissionTrackingId,
      });
      if (response?.status === 200) {
        setCoApplicantFieldsIds([]);
        setCoApplicantNumbers(response?.data?.data?.count);
        await addCoApplicantFields(
          response?.data?.data?.count,
          relationshipOptions,
          salutationList,
          nationalityOptions,
          maritalStatusOptions,
          countryOptions,
          []
        );
        await handleGetApplicationStatus();
      } else {
        setIsAddingCoApplicant(false);
        dispatch(
          setStatusPopup({
            status: "error",
            message: response?.data?.message || "Failed to get co-applicant count",
          })
        );
      }
    } catch (error: any) {
      setIsAddingCoApplicant(false);
      dispatch(
        setStatusPopup({
          status: "error",
          message: error?.response?.data?.message || "Failed to add co-applicant",
        })
      );
    }
  }, [
    addCoApplicantFields,
    countryOptions,
    dispatch,
    handleGetApplicationStatus,
    handleJourneyPennantRelationshipMaster,
    maritalStatusOptions,
    nationalityOptions,
    submissionTrackingId,
  ]);

  const handleDeleteCoApplicantFields = useCallback(async (payload: any) => {
    try {
      const oldTabIndex = tabIndex;
      const resp = await losServices.deleteCoApplicantFields(payload);
      if (resp?.status === 200) {
        setDeleteFieldIds([]);
        // Redux formData is only ever merged into (updateFormData), never
        // pruned, so the deleted co-applicant's basic/address/employment
        // fields stay behind after this refetch — ConsentDetails.tsx still
        // shows that co-applicant as long as any basicCoApplicant{N}* key
        // exists in formData, regardless of what the fresh fetch below finds.
        const deletedCoAppNum = applicants?.[oldTabIndex]?.split(" ")?.[1];
        if (deletedCoAppNum) {
          const coAppRegex = new RegExp(
            `^(basic|address|employment)CoApplicant${deletedCoAppNum}`
          );
          const keysToRemove = Object.keys(formData || {}).filter((key) =>
            coAppRegex.test(key)
          );
          if (keysToRemove.length > 0) {
            dispatch(removeFormDataKeys(keysToRemove));
          }
        }
        refreshDocuments();
        handleFetchApplicationDetail();
        dispatch(setDocumentApplicantType(applicants?.[oldTabIndex - 1]));
        if (oldTabIndex > 0) setTabIndex(oldTabIndex - 1);
      }
    } catch (error: any) {
      dispatch(
        setStatusPopup({
          status: "error",
          message: error?.response?.data?.message,
        })
      );
    }
  }, [tabIndex, refreshDocuments, handleFetchApplicationDetail, dispatch, applicants, setTabIndex, formData]);

  const handleDeleteCoApplicant = useCallback(async (payload: any) => {
    try {
      const resp = await losServices.deleteCoApplicant(payload);
      if (resp?.status === 200) {
        const fieldDeletePayload = {
          trackingId: applicationNumber,
          form_field_ids: deleteFieldIds,
        };
        await handleDeleteCoApplicantFields(fieldDeletePayload);
      }
    } catch (error: any) {
      dispatch(
        setStatusPopup({
          status: "error",
          message: error?.response?.data?.message,
        })
      );
    }
  }, [applicationNumber, deleteFieldIds, dispatch, handleDeleteCoApplicantFields]);

  const handleConfirmDeleteCoApplicant = useCallback(async () => {
    const coAppNum = applicants?.[tabIndex]?.split(" ")?.[1];
    const payload = {
      trackingId: applicationNumber,
      coApplicantType: applicants?.[tabIndex],
    };
    dispatch(
      setStatusPopup({
        status: "warning",
        message: "Are you sure you want to delete this co-applicant?",
        isFunction: true,
        locationFunction: async () => {
          let processioApplicantId = null;
          try {
            const coApplicantResp = await losServices.getCoApplicantDetails({
              trackingId: applicationNumber,
              coApplicantType: applicants?.[tabIndex],
            });
            processioApplicantId = coApplicantResp?.data?.data?.applicantId;
          } catch (err) {
            console.error("Error fetching co-applicant details before delete", err);
          }

          await handleDeleteCoApplicant(payload);

          
        },
      })
    );
  }, [applicationNumber, applicationId, applicants, dispatch, handleDeleteCoApplicant, tabIndex]);

  const handleAddCoApplicantFields = useCallback(
    async (values: any, { setSubmitting }: any) => {
      // if(values[`employmentCoApplicant${coApplicantNumbers}isEmployed`] === "Yes" && !values[`employmentCoApplicant${coApplicantNumbers}employmentType`]) {
      //   setEmpTypeError("Employment Type field is required");
      //   return;
      // }
      if (coApplicantNumbers === 0) {
        try {
          const resp = await documentsServices.saveCoApplicantOneFields();
          if (resp?.status === 200) {
            let updated = coApplicantFields.map((field: any) => {
              // Try exact name match first, then fall back to label match
              const matchingField =
                resp.data.data.find((respField: any) => respField.name === field.name) ||
                resp.data.data.find(
                  (respField: any) =>
                    String(respField.label).toLowerCase() === String(field.label).toLowerCase()
                );
              if (matchingField) return { ...field, id: matchingField.id };
              return field;
            });

            // OtherRelationWithStudent is not pre-registered in saveCoApplicantOneFields.
            // Call store-user-fields for only the fields still missing an id.
            const fieldsWithoutId = updated.filter((f: any) => !f.id);
            if (fieldsWithoutId.length > 0) {
              try {
                const storeResp = await postsServices.addPost(
                  "api/v1/product/store-user-fields",
                  {
                    vertical_step_id: 9,
                    fields: fieldsWithoutId,
                    trackingId: String(applicationNumber),
                  }
                );
                if (storeResp?.status === 201 && Array.isArray(storeResp.data?.data)) {
                  updated = updated.map((f: any) => {
                    if (f.id) return f;
                    const match =
                      storeResp.data.data.find((r: any) => r.name === f.name) ||
                      storeResp.data.data.find(
                        (r: any) =>
                          String(r.label).toLowerCase() === String(f.label).toLowerCase()
                      );
                    return match ? { ...f, id: match.id } : f;
                  });
                }
              } catch {
                // proceed — field will submit with null formFieldId
              }
            }

            await submitCoApplicant({
              values,
              setSubmitting,
              updatedCoApplicantFields: updated,
              respCoApplicantFieldsIds: [],
              getCoApplicantNumbers,
              applicationNumber,
              aadhaarSeedingStatus,
              isCoApplicantPanVerified,
              SalutationList,
              relationshipList: journeyPennantRelationshipMaster,
              fetchUserApplicationData,
              applicationId,
              dispatch,
              handleFetchApplicationDetail,
              handleDeleteCoApplicantFields,
              closeCoApplicantModal: handleCloseCoApplicantModal,
            });
          }
        } catch {
          dispatch(
            setStatusPopup({
              status: "error",
              message: "Something went wrong ! Please try again.",
            })
          );
        }
      } else {
        try {
          const oldCoApplicantFields: any = [...coApplicantFields];
          oldCoApplicantFields.push(...fieldsList(getCoApplicantNumbers + 1));

           const uniqueData = oldCoApplicantFields?.filter((item: any, index: number, self: any) =>
            index === self.findIndex((obj: any) => obj.name === item.name)) || [];

          const resp = await postsServices.addPost("api/v1/product/store-user-fields", {
            vertical_step_id: 9,
            fields: uniqueData,
            trackingId: String(applicationNumber),
          });

          if (resp?.status === 201) {
            const respIds: any = resp.data.data.map((field: any) => field.id);
            setCoApplicantFieldsIds(respIds);

            const updated = coApplicantFields.map((field: any) => {
              const matchingField = resp.data.data.find(
                (respField: any) =>
                  String(respField.label).toLowerCase() ===
                  String(field.label).toLowerCase()
              );
              if (matchingField) return { ...field, id: matchingField.id };
              return field;
            });

            await submitCoApplicant({
              values,
              setSubmitting,
              updatedCoApplicantFields: updated,
              respCoApplicantFieldsIds: respIds,
              getCoApplicantNumbers,
              applicationNumber,
              aadhaarSeedingStatus,
              isCoApplicantPanVerified,
              SalutationList,
              relationshipList: journeyPennantRelationshipMaster,
              fetchUserApplicationData,
              applicationId,
              dispatch,
              handleFetchApplicationDetail,
              handleDeleteCoApplicantFields,
              closeCoApplicantModal: handleCloseCoApplicantModal,
            });
          } else {
            setSubmitting(false);
          }
        } catch (error: any) {
          setSubmitting(false);
          dispatch(
            setStatusPopup({
              status: "error",
              message: error?.response?.data?.message,
            })
          );
        }
      }
    },
    [
      coApplicantNumbers,
      coApplicantFields,
      getCoApplicantNumbers,
      applicationNumber,
      aadhaarSeedingStatus,
      isCoApplicantPanVerified,
      SalutationList,
      fetchUserApplicationData,
      applicationId,
      dispatch,
      handleFetchApplicationDetail,
      handleDeleteCoApplicantFields,
      handleCloseCoApplicantModal,
    ]
  );

  const handlePanVerification = useCallback(
    async (
      field: CoApplicantField,
      values: FormikValues,
      setFieldValue: FormikHelpers<FormikValues>["setFieldValue"]
    ) => {
      setCoApplicantPanLoading(true);
      try {
        const panNumberValue = values?.[field.name] || "";

        let coAppIdx: any = null;
        const match = field.name.match(/^basicCoApplicant(\d+)PanNumber$/);
        if (match) coAppIdx = match[1];

        const firstNameKey = coAppIdx ? `basicCoApplicant${coAppIdx}FirstName` : "FirstName";
        const lastNameKey = coAppIdx ? `basicCoApplicant${coAppIdx}LastName` : "LastName";
        const dobKey = coAppIdx ? `basicCoApplicant${coAppIdx}dob` : "dob";

        const firstNameValue = values?.[firstNameKey] || "";
        const lastNameValue = values?.[lastNameKey] || "";
        const fullNameValue = `${firstNameValue} ${lastNameValue}`.trim();
        const dobValue = values?.[dobKey] || "";

        const piiPublicKey = await fetchSanitizedPiiPublicKey();
        if (!piiPublicKey) {
          dispatch(
            setStatusPopup({
              status: "error",
              message:
                "Unable to load encryption key. Please try again.",
            })
          );
          return;
        }

        const payload = await encryptData(
          {
            panNumber: panNumberValue,
            fullName: fullNameValue,
            dob: dobValue
              ? new Date(dobValue).toLocaleDateString("en-GB")
              : "",
            trackingId: applicationNumber,
            applicantType: `Co-Applicant ${tabIndex + 1}`,
          },
          ["panNumber"],
          piiPublicKey
        );

        const resp = await applicantDetailsServices.verifyPan(payload);

        if (resp.status === 200) {
          if (resp.data?.data?.dateOfBirth === "invalid" && dobValue) {
            setIsCoApplicantPanVerified(false);
            setAadhaarSeedingStatus(resp.data?.data?.aadhaarSeedingStatus);
            dispatch(
              setStatusPopup({
                status: "error",
                message: "Invalid Date of Birth.",
              })
            );
          } else {
            setIsCoApplicantPanVerified(true);
            dispatch(
              setStatusPopup({
                status: "success",
                message: "PAN Verified Successfully",
              })
            );
          }
        } else {
          dispatch(
            setStatusPopup({
              status: "error",
              message:
                resp?.data?.errors?.[0]?.constraints?.matches ||
                "PAN Verification Failed",
            })
          );
        }
      } catch(error: any) {
        console.log(error, "error");
        dispatch(
          setStatusPopup({
              status: "info",
              message: error?.response?.data?.errors?.length > 0 ? error?.response?.data?.errors[0]?.constraints?.matches : error?.response?.data?.message,
          })
        );
      } finally {
        setCoApplicantPanLoading(false);
      }
    },
    [applicationNumber, dispatch, fetchSanitizedPiiPublicKey, tabIndex]
  );

  return {
    coApplicantFields,
    setCoApplicantFields,
    coApplicantFieldsLoading,
    setCoApplicantFieldsLoading,
    isAddingCoApplicant,
    isCoApplicantModalOpen,
    setIsCoApplicantModalOpen,
    getInitialValues,
    coApplicantValidationSchema,
    handleAddCoApplicantFields,
    handleCloseCoApplicantModal,
    handleCoApplicantFormChange,
    handleGetCoApplicantCount,
    handleConfirmDeleteCoApplicant,
    deleteFieldIds,
    setDeleteFieldIds,
    isCoApplicantPanVerified,
    coApplicantPanLoading,
    handlePanVerification,
    aadhaarSeedingStatus,
    SalutationList,
    isPanOtpModalOpen: panOtp.isPanOtpModalOpen,
    setIsPanOtpModalOpen: panOtp.setIsPanOtpModalOpen,
    panOtpData: panOtp.panOtpData,
    setPanOtpData: panOtp.setPanOtpData,
    handlePanOtpSubmit: panOtp.handlePanOtpSubmit,
    handleResendPanOtp: panOtp.handleResendPanOtp,
    handleClosePanOtpModal: panOtp.handleClosePanOtpModal,
    isPanVerified: panOtp.isPanVerified,
    coApplicantFormData,
    getCoApplicantNumbers,
    coApplicantNumbers,
    empTypeError,
    setEmpTypeError
  };
}

